import { useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { useNavPrefs } from "../hooks/useNavPrefs";
import { flattenLeaves } from "./sidebar/utils";
import { useSidebarScrollPreserve } from "./sidebar/useSidebarScrollPreserve";
import SidebarBrand from "./sidebar/SidebarBrand";
import SidebarContextMenu from "./sidebar/SidebarContextMenu";
import SidebarDivider from "./sidebar/SidebarDivider";
import SidebarGroup from "./sidebar/SidebarGroup";
import SidebarLink from "./sidebar/SidebarLink";
import SidebarPinnedSection from "./sidebar/SidebarPinnedSection";

/**
 * Collapsible left-hand sidebar for desktop (≥ lg).
 *
 * Thin orchestrator — all rendering primitives live in ./sidebar/*. This
 * component owns:
 *   - Which group is being hovered (for the collapsed-rail flyout).
 *   - The pin/unpin right-click context menu overlay.
 *   - Resolving pinned `to` paths into full item objects for the pinned
 *     section at the top.
 */
export default function Sidebar({ navItems, brandName = "Talmia" }) {
  const location = useLocation();
  const {
    collapsed, expandedGroups, pinned, hideRoadmap,
    toggleCollapsed, toggleHideRoadmap, toggleGroup, togglePinned, isPinned,
  } = useNavPrefs();

  const [hoverGroup, setHoverGroup] = useState(null);
  const [ctxMenu, setCtxMenu] = useState(null); // { x, y, to, pinned }
  const scrollRef = useSidebarScrollPreserve();
  // Debounce close so the mouse can cross the 4px gap between the group
  // trigger and the portalled flyout without a flicker.
  const hoverCloseTimer = useRef(null);
  const setHoverGroupNow = (key) => {
    if (hoverCloseTimer.current) { clearTimeout(hoverCloseTimer.current); hoverCloseTimer.current = null; }
    setHoverGroup(key);
  };
  const scheduleHoverClose = () => {
    if (hoverCloseTimer.current) clearTimeout(hoverCloseTimer.current);
    hoverCloseTimer.current = setTimeout(() => { setHoverGroup(null); hoverCloseTimer.current = null; }, 120);
  };

  // Resolve pinned `to` paths back to full items with icons/colours.
  // Skip disabled items so a stale pin from a now-TBD route doesn't render.
  const leaves = flattenLeaves(navItems);
  const leavesByPath = leaves.reduce((acc, it) => { acc[it.to] = it; return acc; }, {});
  const pinnedItems = pinned.map((p) => leavesByPath[p]).filter((it) => it && !it.disabled);

  const openContextMenu = (e, to) => {
    e.preventDefault();
    setCtxMenu({ x: e.clientX, y: e.clientY, to, pinned: isPinned(to) });
  };
  const closeContextMenu = () => setCtxMenu(null);

  const width = collapsed ? "w-16" : "w-60";

  return (
    <>
      <aside
        data-testid="sidebar"
        data-collapsed={collapsed}
        data-inverse="sidebar" className={`hidden lg:flex sticky top-0 z-50 h-screen ${width} shrink-0 flex-col border-r border-white/5 bg-[#0B1224]/80 backdrop-blur-xl transition-[width] duration-200`}
      >
        <SidebarBrand
          brandName={brandName}
          collapsed={collapsed}
          onCollapse={toggleCollapsed}
          hideRoadmap={hideRoadmap}
          onToggleRoadmap={toggleHideRoadmap}
        />

        <div ref={scrollRef} className="flex-1 overflow-y-auto overflow-x-visible py-3 space-y-1">
          <SidebarPinnedSection
            items={pinnedItems}
            collapsed={collapsed}
            onTogglePin={togglePinned}
            onContextMenu={openContextMenu}
          />

          <div className="px-2 space-y-0.5">
            {navItems.map((n) => {
              if (n.type === "divider") {
                return <SidebarDivider key={n.key} item={n} collapsed={collapsed} />;
              }
              if (n.type === "link") {
                return (
                  <SidebarLink
                    key={n.to}
                    item={n}
                    collapsed={collapsed}
                    location={location}
                    isPinned={isPinned}
                    onTogglePin={() => togglePinned(n.to)}
                    onContextMenu={(e) => openContextMenu(e, n.to)}
                  />
                );
              }
              // group — when roadmap is hidden, groups default to CLOSED
              // (the "clean nav" mode); otherwise default OPEN.
              return (
                <SidebarGroup
                  key={n.key}
                  group={n}
                  collapsed={collapsed}
                  isOpen={expandedGroups[n.key] ?? !hideRoadmap}
                  hoverGroup={hoverGroup}
                  location={location}
                  isPinned={isPinned}
                  togglePinned={togglePinned}
                  toggleGroup={toggleGroup}
                  onHover={() => collapsed && setHoverGroupNow(n.key)}
                  onLeave={() => collapsed && scheduleHoverClose()}
                  onContextMenu={openContextMenu}
                />
              );
            })}
          </div>
        </div>

        {/* Footer — expand button when collapsed. Bolder TEAL to match top chevron. */}
        {collapsed && (
          <div className="border-t border-white/5 p-2 flex justify-center">
            <button
              type="button"
              onClick={toggleCollapsed}
              data-testid="sidebar-expand-toggle"
              aria-label="Expand sidebar"
              className="w-10 h-10 grid place-items-center rounded-md text-teal-300 hover:text-teal-100 hover:bg-teal-500/[0.10]"
            >
              <ChevronRight className="w-5 h-5 stroke-[3]" />
            </button>
          </div>
        )}
      </aside>

      {ctxMenu && (
        <SidebarContextMenu
          x={ctxMenu.x}
          y={ctxMenu.y}
          pinned={ctxMenu.pinned}
          onToggle={() => togglePinned(ctxMenu.to)}
          onClose={closeContextMenu}
        />
      )}
    </>
  );
}
